import { focusWhenReady } from '../helpers/autofocus'
import { composeCleanup } from '../helpers/cleanup'
import { setupDraftInput } from '../helpers/draft-input'
import { setupPageComponent } from '../helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type InputHook = {
  readonly el: HTMLElement
  inputCleanup?: () => void
}

type UpdatableInput = HTMLElement & {
  readonly updateComplete: Promise<unknown>
}

// Private functions -----------------------------------------------------------

// Recognizes controls whose shadow input may render after the hook mounts.
const isUpdatableInput = (host: HTMLElement): host is UpdatableInput =>
  'updateComplete' in host && host.updateComplete instanceof Promise

// Preserves composition metadata when an inner input event crosses the host.
const relayedInput = (event: Event): Event =>
  typeof window.InputEvent === 'function' && event instanceof window.InputEvent
    ? new window.InputEvent('input', {
        bubbles: true,
        composed: true,
        data: event.data,
        inputType: event.inputType,
        isComposing: event.isComposing
      })
    : new Event('input', { bubbles: true, composed: true })

// Emits one host event for the draft adapter and form carrier.
const relayInput = (host: HTMLElement, event: Event): void => {
  event.stopPropagation()
  host.dispatchEvent(relayedInput(event))
}

// LiveView reads selection positions from the focused host before calling its
// setSelectionRange method; Web Awesome only exposes that method, not the positions.
const setupSelection = (
  host: HTMLElement,
  input: HTMLInputElement
): (() => void) => {
  const properties = (['selectionStart', 'selectionEnd'] as const).filter(
    (property) => !(property in host)
  )

  for (const property of properties) {
    Object.defineProperty(host, property, {
      configurable: true,
      get: () => input[property]
    })
  }

  return () => {
    for (const property of properties) {
      Reflect.deleteProperty(host, property)
    }
  }
}

// Connects the native input once and releases its event and selection bridges.
const setupNativeInput = (host: HTMLElement): (() => void) => {
  let active = true
  let inputCleanup: (() => void) | undefined

  // Waits for the inner control without attaching after destruction.
  const attach = (): void => {
    if (!active || inputCleanup) {
      return
    }

    const input = host.shadowRoot?.querySelector('input')

    if (!input) {
      return
    }

    // Relays native edits through the host's form contract.
    const handleInput = (event: Event): void => relayInput(host, event)

    input.addEventListener('input', handleInput)
    inputCleanup = composeCleanup(setupSelection(host, input), () =>
      input.removeEventListener('input', handleInput)
    )
  }

  attach()

  if (!inputCleanup && isUpdatableInput(host)) {
    host.updateComplete.then(attach)
  }

  return () => {
    active = false
    inputCleanup?.()
    inputCleanup = undefined
  }
}

// Waits until autofocus can reach an upgraded and rendered control.
const whenRendered = async (host: HTMLElement): Promise<void> => {
  await customElements.whenDefined(host.localName)
  await (isUpdatableInput(host) ? host.updateComplete : undefined)
}

// Public hook -----------------------------------------------------------------

export const FacadeInput = {
  // Waits for lazily inserted controls before reading their shadow input.
  mounted(this: InputHook): void {
    this.inputCleanup?.()
    this.inputCleanup = setupPageComponent(this.el, (element) =>
      composeCleanup(setupNativeInput(element), setupDraftInput(element))
    )
    focusWhenReady(this.el, whenRendered(this.el))
  },

  // Cancels pending setup and releases the native input and draft bindings.
  destroyed(this: InputHook): void {
    this.inputCleanup?.()
    this.inputCleanup = undefined
  }
}
